import { useEffect, useState } from "react";

// True when viewport is at-or-below the given breakpoint.
//
// Breakpoint convention used across the app (matches Tailwind defaults):
//
//   mobile   : viewport <= 768  → useIsMobile() returns true
//              (iPhone, small Android, iPad portrait)
//   tablet   : viewport 769..1023
//              (iPad landscape, 10-inch tablets, small laptop)
//   desktop  : viewport >= 1024 → useIsMobile() returns false
//
// IMPORTANT: default 769 (not 768) so iPad portrait — which is exactly
// 768px — qualifies as mobile. The 2026-05-27 audit found iPad portrait
// was getting the 10-button desktop matches table because the cutoff
// was 768 with maxWidthPx-1=767 in the MQ. Bumped to 769 so MQ becomes
// max-width:768 and 768 itself matches.
//
// Used to render entirely different mobile / desktop component trees
// (MobileDashboard, MobileList, ScorerChat fullscreen) rather than
// fight responsive Tailwind to collapse a 3-pane desktop scoring
// layout into a single-column phone layout.
//
// Call useIsMobile(1024) when the consuming component should treat
// tablet-and-below as the "mobile" surface (e.g. ScorerChat fullscreen
// matches the lg: sidebar-drawer cutoff).
export default function useIsMobile(maxWidthPx = 769) {
  const query = `(max-width: ${maxWidthPx - 1}px)`;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [query]);
  return isMobile;
}
